import { getDefaultStore } from 'jotai';
import { ErrorResponse, type User } from 'oidc-client-ts';

import { readAnyOfflineRecordingFlagDecision } from '@/app-shell/flags/offline-recording-flag-cache';
import { offlineConnectivityStateAtom } from '@/atoms/offline-recording';
import { store as reduxStore } from '@/store';
import { desktopAuthBridge } from '@/tauri/auth0';
import { getPersistedAuth0DesktopToken } from '@/tauri/auth0/persisted-token';
import { getKeycloakUserManager } from '@/tauri/keycloak/oidcClient';
import { isTauri } from '@/tauri/utils';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { logger } from '@/utils/logger/logger';
import {
  extractKindeUserIdFromAuth0Token,
  extractRegionFromAccessToken,
  extractUserRefIdFromAuth0Token,
} from '@heidi/auth/tokens';
import {
  getExternalLaunchProvider,
  getExternalLaunchToken,
} from '@heidi/integrations/ehr/data/atoms';

import { keycloakSessionBridgeAtom, kindeDesktopBridgeAtom } from '@heidi/auth/state';
import { getEffectiveAuthProvider } from './effectiveAuthProvider';
import type { AuthSessionMetadata } from './types';

/**
 * Local budget on the Auth0-desktop bridge's `getAccessToken` call.
 *
 * The Rust plugin's own network timeout is ~10s; the FE-side global
 * `AUTH_FETCH_TIMEOUT_MS` (see `getAccessToken.ts`) is also 10s and
 * wraps the whole `readFromProviderSourceOfTruth` call. On a
 * black-holed / captive-portal network (`navigator.onLine === true`,
 * packets dropped) the Rust refresh hangs the full ~10s and the FE
 * global fires first — the outer `catch` in `readAuth0Desktop` never
 * runs, `offlineDesktopFallback` is dead code, and every boot-retry
 * loses the same race → permanent `'loading'` skeleton.
 *
 * Cap the bridge call locally so it always loses to the global. The
 * catch runs with a few seconds of headroom for the fallback to
 * consult `getAuthSession()` (itself locally bounded — see
 * `AUTH0_DESKTOP_FALLBACK_SESSION_TIMEOUT_MS`).
 */
export const AUTH0_DESKTOP_BRIDGE_TIMEOUT_MS = 7_000;

/**
 * Local budget on the fallback's `getAuthSession` read.
 *
 * `getAuthSession` is NOT a cheap local IPC: the Rust `auth_session`
 * command attempts a network refresh when there is no cached
 * session (see `tauri-plugin-auth0/src/commands.rs:141-161`). It
 * also joins any concurrent Rust refresh via the plugin's own
 * single-flight — so when the outer `getAccessToken` bridge call
 * has already triggered the Rust refresh, this read waits on the
 * SAME in-flight and resolves at the Rust connect deadline
 * (`tauri-plugin-auth0/src/oauth.rs:21` — 10s).
 *
 * The cap therefore has to cover: (bridge cap 7s) + (join wait to
 * hit the Rust 10s connect deadline ≈ 3s) with headroom. 4s gives
 * the join room to resolve at t≈10s from `getAccessToken()` entry
 * (7s bridge cap → catch → up to 4s here = 11s), staying inside
 * the Auth0-desktop provider budget of 13s. On timeout the wrapper
 * resolves to `null` (safe-direction — fallback skipped, transient
 * propagates, boot-retry loop keeps trying). Same const feeds
 * `isSignedOutViaAuthSession()` so both fallback readers share the
 * budget.
 */
export const AUTH0_DESKTOP_FALLBACK_SESSION_TIMEOUT_MS = 4_000;

/**
 * Race `promise` with a timeout that resolves `null` when it fires.
 * Used for the fallback's session read where a hang must map to a
 * safe skip. Clears the timer when the promise settles first so we
 * don't leak a pending timeout callback.
 */
async function raceSessionRead<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<null>((resolve) => {
    timeoutId = setTimeout(() => resolve(null), ms);
  });
  try {
    return await Promise.race([
      promise.finally(() => {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
      }),
      timeoutPromise,
    ]);
  } catch {
    // A throw from `promise` on this path is treated as null too —
    // the caller then does the same "skip fallback" as on timeout.
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    return null;
  }
}

/**
 * Race `promise` with a timeout that rejects with the given marker.
 * Used to cap the bridge call so the outer `catch` in
 * `readAuth0Desktop` reaches the offline fallback before the global
 * `AUTH_FETCH_TIMEOUT_MS` fires.
 */
async function raceOrTimeout<T>(
  promise: Promise<T>,
  ms: number,
  marker: string
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(marker)), ms);
  });
  return Promise.race([
    promise.finally(() => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }),
    timeoutPromise,
  ]);
}

/**
 * OAuth2 error codes that indicate the session is dead. Two RFC layers
 * contribute:
 *
 * - Token endpoint (RFC 6749 §5.2): `invalid_grant` (refresh token
 *   revoked/expired), `invalid_client` (client revoked), and
 *   `unauthorized_client` (client no longer authorized for the grant).
 * - Authorize endpoint under `prompt=none` (OIDC Core 1.0 §3.1.2.6):
 *   `login_required` / `interaction_required` / `consent_required` —
 *   authoritative IdP no-session statements returned when a silent
 *   iframe auth attempt cannot complete without user interaction. Same
 *   semantic class as `invalid_grant` from the token endpoint.
 *
 * Without the OIDC-layer codes, a Keycloak realm configured without
 * refresh tokens (silent iframe auth is its only refresh path) would
 * loop transient forever, firing a new iframe auth attempt per retry.
 *
 * Other ErrorResponse values (invalid_request, unsupported_grant_type,
 * invalid_scope) are typically misconfiguration rather than
 * session-death — treating them as definitive would log users out on a
 * deploy bug, so we leave them as transient. Iframe timeouts (a
 * network / infrastructure phenomenon, not an IdP verdict) also stay
 * transient.
 */
const KEYCLOAK_DEFINITIVE_OIDC_ERROR_CODES = new Set([
  'invalid_grant',
  'invalid_client',
  'unauthorized_client',
  'login_required',
  'interaction_required',
  'consent_required',
]);

function isDefinitiveOidcError(err: unknown): boolean {
  return (
    err instanceof ErrorResponse &&
    typeof err.error === 'string' &&
    KEYCLOAK_DEFINITIVE_OIDC_ERROR_CODES.has(err.error)
  );
}

/**
 * Single-flight guard for the Keycloak-desktop expired-at-load renewal.
 *
 * The UserManager's `automaticSilentRenew` does NOT auto-renew a token
 * that is already expired at load time: `SilentRenewService.start()`
 * subscribes only to the pre-expiry `accessTokenExpiring` event, and
 * `AccessTokenEvents.load()` cancels that timer when the loaded
 * `duration <= 0`. Without an explicit `signinSilent()` from the
 * cache, `authStateAtom` would hold `'loading'` indefinitely on cold
 * boot (boot-retry and focus both re-read the same expired user).
 *
 * The guard is in-flight (not once-per-process): first caller starts
 * the renew, same-generation concurrent callers await the same
 * promise, and the slot clears once the attempt settles so a later
 * trigger can re-attempt after a transient failure. A permanent
 * once-per-process guard was considered and rejected — it would
 * strand a user whose single attempt failed on a transient network
 * blip.
 *
 * The slot is generation-stamped. `clearAuthCache()` bumps the
 * cache generation but cannot see this module-level slot, so a
 * post-logout fetch (new generation) that arrived while a
 * pre-logout renewal was still in flight would otherwise join the
 * stale attempt, pass `launchFetch`'s generation check (it captured
 * the new generation), flip atoms to `'authenticated'`, and
 * mirror-write the dead session's token into Redux AFTER
 * `dispatch.auth.clear()` — resurrecting a logged-out session.
 * Cross-generation callers throw `AUTH_SESSION_TRANSIENT`
 * immediately and MUST NOT start a fresh `signinSilent` while the
 * stale slot is occupied (concurrent grant = the rotation race the
 * guard exists to prevent). Once the stale attempt settles the
 * slot clears and the next fetch reads reality fresh.
 *
 * Residual accepted risk (documented ms-scale, not reachable by
 * realistic focus events): oidc-client-ts re-stores the renewed
 * user when the stale `signinSilent` resolves — a fetch landing in
 * the milliseconds between that resolution and the end-session
 * navigation unloading the webview could read the re-stored user.
 * The end-session navigation itself unloads the webview, so this
 * window is bounded by the navigation, not by a user action.
 *
 * Serialized refresh-grant attempts are rotation-safe: rotation
 * breaks on concurrent reuse of one refresh token, not on sequential
 * grants each reading the newest stored token. The guard is what
 * enforces the serialization.
 */
interface ExpiredRenewSlot {
  attempt: Promise<User | null>;
  generation: number;
}
let expiredUserRenewInFlight: ExpiredRenewSlot | null = null;

async function performExpiredUserRenew(
  userManager: { signinSilent: () => Promise<User | null> },
  generation: number
): Promise<User | null> {
  if (expiredUserRenewInFlight !== null) {
    if (expiredUserRenewInFlight.generation === generation) {
      // Same-generation join — legitimate serialisation.
      return expiredUserRenewInFlight.attempt;
    }
    // Cross-generation fetch (post-logout / post-`clearAuthCache`
    // trigger arriving while a stale attempt is still in flight).
    // Do NOT join — the stale attempt's result belongs to the
    // previous session. Do NOT start a fresh `signinSilent` either
    // — a concurrent grant is exactly the rotation race the guard
    // prevents. Throw transient; the caller's `launchFetch`
    // classifies transient as "preserve state" — combined with the
    // new generation's own cleared atoms, the logged-out state
    // survives. Once the stale attempt settles the slot clears
    // (its `.finally`), and the next same-generation fetch reads
    // reality fresh.
    throw new Error('AUTH_SESSION_TRANSIENT');
  }
  const attempt = userManager.signinSilent().finally(() => {
    if (expiredUserRenewInFlight?.attempt === attempt) {
      expiredUserRenewInFlight = null;
    }
  });
  expiredUserRenewInFlight = { attempt, generation };
  return attempt;
}

/**
 * @internal — for tests that need to isolate the single-flight guard
 * between cases. Production code MUST NOT call this: clearing the guard
 * mid-flight would re-enable the race the guard prevents.
 */
export function __resetKeycloakExpiredRenewGuardForTests(): void {
  expiredUserRenewInFlight = null;
}

export interface ReadProviderOptions {
  forceRefresh: boolean;
  /**
   * Aborts the underlying fetch when the cache's timeout fires. Only the
   * web fetch branches (Auth0 web, Keycloak web) plumb this — desktop
   * branches accept the option but ignore it because their underlying
   * calls (Tauri IPC, Kinde SDK, oidc-client-ts) don't support cancellation.
   */
  signal?: AbortSignal;
  /**
   * Cache generation the fetch was launched under. Plumbed from
   * `launchFetch` for provider branches that maintain their own
   * in-flight slots outside the cache module (today: the
   * Keycloak-desktop expired-user single-flight — see
   * `performExpiredUserRenew`). Those slots survive `clearAuthCache()`
   * which bumps the generation but cannot reach into module-level
   * state; without a stamp, a post-logout fetch could join a
   * pre-logout attempt and resurrect a dead session. Provider
   * branches that don't hold cross-generation state ignore this
   * field.
   */
  generation?: number;
}

/**
 * Reads the current session from the active provider's source of truth.
 *
 * Returns:
 * - `AuthSessionMetadata` on a successful read.
 * - `null` when the source definitively says "no session" without throwing
 *   (Auth0 desktop's Rust plugin has no token; Kinde / Keycloak desktop
 *   bridges return null). Maps to the cache's bare-null outcome.
 *
 * Throws:
 * - `Error('AUTH_SESSION_INVALID')` on a definitive session-dead signal.
 * - `Error('AUTH_SESSION_TRANSIENT')` on retryable infra failure. The
 *   cache classifies this and any other thrown error as transient.
 *
 * Dispatch:
 * - `AuthProvider.AUTH0` → Auth0 web (`/api/auth0-session`)
 * - `AuthProvider.AUTH0_DESKTOP` → Auth0 desktop (Rust plugin)
 * - `AuthProvider.KINDE` + `isTauri()` → Kinde desktop (bridge atom)
 * - `AuthProvider.KINDE` + `!isTauri()` → Kinde web (stub — null until the
 *   `add-kinde-session-invalidation` sibling lands)
 * - `AuthProvider.KEYCLOAK` + `isTauri()` → Keycloak desktop (calls
 *   `getKeycloakUserManager()` directly — no bridge atom; the UserManager
 *   is a module-level singleton accessible without React)
 * - `AuthProvider.KEYCLOAK` + `!isTauri()` → Keycloak web (asymmetric:
 *   non-force direct fetch, force via bridge atom)
 * - `AuthProvider.FHIR` → null (FHIR sessions aren't refreshable; the
 *   axios interceptor's external-launch-token branch handles FHIR tokens)
 * - Unknown / null provider → null (no positive evidence — bare null)
 *
 * Provider determination is delegated to `getEffectiveAuthProvider()` —
 * cookie-first on web (so the Kinde→Auth0 migration window resolves
 * correctly while Redux is still catching up), Redux on desktop.
 */
export async function readFromProviderSourceOfTruth(
  options: ReadProviderOptions
): Promise<AuthSessionMetadata | null> {
  // The provider is captured once at dispatch entry. If a logout (or
  // a logout-then-login-as-a-different-provider) lands while the chosen
  // branch is still in flight, this fetch continues running with the
  // original provider's contract — `launchFetch`'s generation guard
  // (see `getAccessToken.ts`) discards the resulting payload before it
  // reaches the cache, so a stale response can't poison a re-authed
  // session.
  // A FHIR-launched tab authenticates with its launch token alone. The
  // cookie/Redux provider can be left over from an earlier login in this
  // browser, and reading that provider's session here would turn its
  // absence into a definitive invalid and log the launched tab out.
  if (getExternalLaunchProvider() === AuthProvider.FHIR && getExternalLaunchToken()) {
    return null;
  }

  const tauri = isTauri();
  const provider = getEffectiveAuthProvider();

  switch (provider) {
    case AuthProvider.AUTH0:
      return readAuth0Web(options);
    case AuthProvider.AUTH0_DESKTOP:
      return readAuth0Desktop(options);
    case AuthProvider.KINDE:
      return tauri ? readKindeDesktop(options) : readKindeWeb(options);
    case AuthProvider.KEYCLOAK:
      return tauri ? readKeycloakDesktop(options) : readKeycloakWeb(options);
    case AuthProvider.FHIR:
      // FHIR sessions aren't refreshable; the external-launch-token presence
      // gate in the axios interceptor handles FHIR tokens. Explicit
      // return so the null-provider gate below can't accidentally
      // capture FHIR — a FHIR user with a persisted token would
      // otherwise land in the desktop transient branch and hang.
      return null;
    default:
      // Null / unrecognised provider. On desktop, a persisted token
      // signals a returning user whose provider hasn't been resolved
      // yet (legacy installs that logged in before the provider was
      // persisted, or a rehydration-in-progress state) — bare-null
      // here would flash a definitive signed-out state on every cold
      // boot. Treat as transient so the boot-retry loop covers the
      // provider-resolution window.
      //
      // On web, keep bare-null unconditionally: an anonymous first
      // visit has a null provider and MUST resolve to
      // `'unauthenticated'` promptly — a transient there would hang
      // every logged-out visitor's boot in `'loading'`.
      //
      // Desktop with NO persisted token is a fresh install (genuinely
      // signed out) — also bare-null.
      if (tauri && reduxStore.getState().auth.token) {
        throw new Error('AUTH_SESSION_TRANSIENT');
      }
      return null;
  }
}

// ---------------------------------------------------------------------------
// Auth0
// ---------------------------------------------------------------------------

interface Auth0SessionShape {
  user: { sub: string; email?: string; [key: string]: unknown } | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  region?: string;
  // Optional server-provided reason on a definitive session-invalid
  // response (e.g. `access_denied`, `session_expired`, `revoked`).
  // Piped through the thrown error message so the cache's failure
  // logger can emit it as structured context — helps root-cause
  // fleet-wide forced-logout spikes from log queries alone.
  reason?: string | null;
  refreshCause?: 'forced' | 'expiry';
}

export async function readAuth0Web({
  forceRefresh,
  signal,
}: ReadProviderOptions): Promise<AuthSessionMetadata | null> {
  const url = forceRefresh ? '/api/auth0-session?refresh=true' : '/api/auth0-session';
  const response = await fetch(url, {
    credentials: 'include',
    cache: 'no-store',
    signal,
  });

  if (response.status === 503) {
    throw new Error('AUTH_SESSION_TRANSIENT');
  }
  if (!response.ok) {
    // Any other non-OK is treated as transient (NOT bare-null) — an
    // infrastructure blip (Vercel 502, Auth0 504, generic 500) is not
    // positive evidence the session is dead. The cache preserves the
    // existing token; the axios 401-retry handler suppresses logout.
    throw new Error('AUTH_SESSION_TRANSIENT');
  }

  const body = (await response.json()) as unknown;

  // Shape validation FIRST. A 200 that lacks a boolean
  // `isAuthenticated` — an empty body, an error envelope, an
  // upstream JSON coercion — is an infrastructure anomaly, not
  // session-death evidence. Under a raw cast, `!body.isAuthenticated`
  // was true for `{}` and every unknown-shape body, classifying
  // DEFINITIVE and firing the full destructive forced logout
  // (cookie deletion, IndexedDB wipe, analytics reset, toast)
  // fleet-wide during a deploy skew or proxy hiccup. Same
  // false-positive-logout hardening the deleted `useAuth0Session`
  // fetcher had. Definitive fires only on a real boolean `false`
  // below.
  if (
    body === null ||
    typeof body !== 'object' ||
    typeof (body as { isAuthenticated?: unknown }).isAuthenticated !== 'boolean'
  ) {
    throw new Error('AUTH_SESSION_TRANSIENT');
  }
  const session = body as Auth0SessionShape;

  if (!session.isAuthenticated) {
    // Definitive: BE confirmed the session is invalid. Suffix the
    // server-provided `reason` when present so downstream failure
    // logging can emit it as structured context. Delimiter is
    // ':reason=' — chosen to be readable in raw log tail and easy
    // to split.
    const reason =
      typeof session.reason === 'string' && session.reason.length > 0
        ? `:reason=${session.reason}`
        : '';
    throw new Error(`AUTH_SESSION_INVALID${reason}`);
  }
  if (typeof session.accessToken !== 'string') {
    // 200 + isAuthenticated:true + non-string accessToken (missing key,
    // null, or any other shape) is a contract violation. Guard on the
    // POSITIVE shape rather than `=== null` so a body with the key
    // absent (accessToken === undefined) can't reach success and
    // mirror-write `undefined` into Redux — wiping the valid stored
    // token. Treat as transient rather than crashing the cache.
    throw new Error('AUTH_SESSION_TRANSIENT');
  }

  if (session.refreshCause) {
    logger.info('[auth-cache] refresh exchange performed', {
      cause: session.refreshCause,
    });
  }

  // Derive userRefId / kindeUserId from the JWT — these aren't in the
  // /api/auth0-session response body, but every consumer that reads the
  // session metadata expects them (today's useAuth0Session does the same
  // JWT decode at use-site). Doing it here once means the cached metadata
  // is consumption-ready and we don't re-decode on every read.
  return {
    accessToken: session.accessToken,
    user: session.user,
    userRefId: extractUserRefIdFromAuth0Token(session.accessToken) ?? null,
    kindeUserId: extractKindeUserIdFromAuth0Token(session.accessToken) ?? null,
    region: session.region ?? extractRegionFromAccessToken(session.accessToken) ?? null,
  };
}

export async function readAuth0Desktop({
  forceRefresh,
}: ReadProviderOptions): Promise<AuthSessionMetadata | null> {
  let result: Awaited<ReturnType<typeof desktopAuthBridge.getAccessToken>> | null;
  try {
    // Locally cap the bridge call so the outer `catch` reaches the
    // fallback logic below on a black-holed network. See
    // `AUTH0_DESKTOP_BRIDGE_TIMEOUT_MS` above for the full rationale.
    // On timeout this throws `AUTH_DESKTOP_BRIDGE_TIMEOUT`, which is
    // handled by the same catch block as any other bridge failure.
    result = await raceOrTimeout(
      desktopAuthBridge.getAccessToken({ forceRefresh }),
      AUTH0_DESKTOP_BRIDGE_TIMEOUT_MS,
      'AUTH_DESKTOP_BRIDGE_TIMEOUT'
    );
  } catch (error) {
    if (forceRefresh) {
      // A forced refresh that fails must never resend the token that
      // just produced a 401 — skip the persisted-token fallback
      // entirely. Signed-out disambiguation is kept: the Rust
      // `auth_get_access_token` command returns
      // `Err(RefreshTokenUnavailable)` when the user is genuinely
      // signed out (no refresh token in the keychain), and without
      // this check that rethrow would classify as transient — no
      // retry, no logout, `authStateAtom` parked at 'loading' forever.
      if (await isSignedOutViaAuthSession()) {
        return null;
      }
      // Anything else (offline, a transient Rust/network failure, an
      // unclear session shape) propagates so the cache classifies it
      // transient: no retry, no logout, the cached authenticated
      // state is preserved.
      throw error;
    }
    // Non-forced (boot, the expiry loop): unchanged — the offline
    // persisted-token fallback still applies here.
    const fallback = await offlineDesktopFallback();
    if (fallback !== null) return fallback;
    if (await isSignedOutViaAuthSession()) {
      return null;
    }
    throw error;
  }

  const token = result?.accessToken ?? null;
  if (token !== null) {
    return { accessToken: token, region: extractRegionFromAccessToken(token) ?? null };
  }

  if (forceRefresh) {
    // The Rust command resolves Ok only via
    // `AccessTokenResponse::from_session`, which always carries a
    // token — a resolved response with none is a contract violation,
    // not a sign-out signal. Never take the persisted-token fallback
    // here: that fallback is exactly the bearer that just 401'd, and
    // resending it would retry once and then log out on the second
    // 401. Thrown here, not inside the try above, so this never
    // reaches the catch's disambiguation or its extra Rust session
    // call — the cache classifies it transient (no retry, no logout).
    throw new Error('AUTH_DESKTOP_FORCED_REFRESH_NO_TOKEN');
  }

  // Non-forced, no token: unchanged — positively offline, matching
  // Rust session shape, and a persisted token together resurrect the
  // provider restore's boot semantics; otherwise bare-null (genuine
  // sign-out).
  return (await offlineDesktopFallback()) ?? null;
}

/**
 * Reads the Rust session-shape and returns `true` when it positively
 * reports "signed out with no refresh token". Any other shape
 * (`isAuthenticated: true`, `hasRefreshToken: true`, or a throw from
 * the IPC call) returns `false` — the caller then propagates its
 * transient signal. The check is only invoked from the
 * `readAuth0Desktop` catch, so a false here maps to "unknown, stay
 * transient" (safe-direction).
 */
async function isSignedOutViaAuthSession(): Promise<boolean> {
  // Bound the read for the same reason `offlineDesktopFallback`
  // does — on a black-holed network the underlying `auth_session`
  // command hangs. On timeout / throw, `raceSessionRead` resolves
  // `null`; report `false` (safe direction — caller propagates the
  // original transient signal).
  const session = await raceSessionRead(
    desktopAuthBridge.getAuthSession(),
    AUTH0_DESKTOP_FALLBACK_SESSION_TIMEOUT_MS
  );
  if (session === null) return false;
  return session.isAuthenticated === false && session.hasRefreshToken === false;
}

/**
 * Positively-offline signal for the Auth0-desktop auth fallback.
 *
 * The auth resurrection path is safe ONLY when we have direct
 * evidence the device is offline. Two independent signals — either
 * suffices:
 *
 *   1. `navigator.onLine === false` — the browser reports no
 *      network. Cheap, always available.
 *   2. The Rust reachability probe has reported a positively-offline
 *      verdict: `offlineConnectivityStateAtom` is `'offline'` or
 *      `'backend_unreachable'`. Any other value — `'unknown'` or
 *      `'online_ready_to_sync'` — is NOT offline. `'unknown'` in
 *      particular must be excluded: the flag-cache arming check
 *      (`readAnyOfflineRecordingFlagDecision()`) reads an ANY-user
 *      decision with a 7-day TTL, while the Rust probe
 *      (`useOfflineConnectivitySync`) arms off the CURRENT user's
 *      per-user decision. On a shared machine where a previous
 *      user had the flag armed and the current user does not (or
 *      after a kill-switch flip), the arming check reads `true`
 *      but the probe never runs, so the atom stays at `'unknown'`
 *      for the whole session. Under the old
 *      `connectivity !== 'online_ready_to_sync'` predicate that
 *      resurrected dead sessions on every online launch in that
 *      cohort. The `readAnyOfflineRecordingFlagDecision()` gate
 *      is retained as belt-and-braces — the whitelist alone is
 *      what makes the check safe, but the flag gate stays as a
 *      second independent filter for minimal diff.
 *
 * NOTE — do NOT reuse `isDesktopOfflineNetworkMode()` here. That
 * helper is deliberately pessimistic (returns `true` when the atom
 * is `'unknown'`) for its original XHR-queueing consumers — don't
 * send through an unknown link — and using it as an auth-
 * resurrection gate is unsafe: on flag-off / kill-switched clients
 * it classifies every online session as "offline" for its entire
 * lifetime.
 *
 * Residual accepted risk: flag-off clients rely solely on
 * `navigator.onLine`. If the browser lies (WKWebView with a stale
 * cached value while genuinely offline), the fallback is skipped
 * → boot degrades to the `'loading'` skeleton and recovers on
 * focus/reconnect. The provider-level Redux restore still fires
 * for axios, so this is a safe-direction degradation, not a data-
 * loss risk. Offline-dependent users have the flag on by
 * definition.
 */
function isPositivelyOffline(): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }
  const flagArmed = readAnyOfflineRecordingFlagDecision();
  if (!flagArmed) return false;
  const connectivity = getDefaultStore().get(offlineConnectivityStateAtom);
  return connectivity === 'offline' || connectivity === 'backend_unreachable';
}

/**
 * Persisted-session offline fallback for Auth0 desktop.
 *
 * Returns a success shape when ALL of these hold, otherwise `null`:
 *
 *   1. Positively offline — see `isPositivelyOffline()` above for
 *      why bare `isDesktopOfflineNetworkMode()` was unsafe as an
 *      auth gate.
 *   2. `desktopAuthBridge.getAuthSession()` reports the Rust
 *      session shape matching the provider-level restore's guards:
 *      `isAuthenticated === false && hasRefreshToken === true`. A
 *      session with no refresh token (logout-on-exit, wiped
 *      keychain, fresh install) MUST NOT take this fallback
 *      regardless of connectivity — those are genuine sign-outs,
 *      not offline blips.
 *   3. `getPersistedAuth0DesktopToken()` yields a persisted token.
 *
 * If `getAuthSession()` itself throws, the fallback SHALL NOT fire.
 * The Rust command is not a cheap in-process read — with no cached
 * session it attempts a network refresh — but the outer
 * `readAuth0Desktop` catch already carries the transient signal
 * from the original bridge failure. Returning a persisted token
 * here on top of a `getAuthSession()` failure would resurrect a
 * dead session with no positive evidence that a session existed
 * at all. `null` is returned; the caller (readAuth0Desktop) then
 * either propagates its original bridge error (catch branch →
 * transient) or falls through to bare-null (try branch).
 *
 * ### Expected-degenerate expiry loop — DO NOT "fix"
 *
 * The persisted token's `exp` claim is typically past by the time
 * we relaunch offline. The cache therefore stores a slot with an
 * already-expired `expiresAt`; every subsequent `getAccessToken()`
 * misses the cache-hit branch, calls `readAuth0Desktop` again, the
 * bridge fails offline again, and this fallback fires again. The
 * transient classification leaves `cached` and `authStateAtom` in
 * `'authenticated'` — the app stays usable offline and self-heals
 * on the first successful refresh after reconnect. This is the
 * offline-cold-relaunch behaviour the provider-level restore
 * established, now reproduced through the cache path.
 */
async function offlineDesktopFallback(): Promise<AuthSessionMetadata | null> {
  if (!isPositivelyOffline()) return null;

  // Bound the session read locally. `getAuthSession` attempts a
  // network refresh when there is no cached session, so on the same
  // hanging network that motivates this fallback the read itself
  // hangs. `raceSessionRead` resolves `null` on timeout — safe
  // direction: fallback skipped, outer catch propagates transient,
  // boot retry re-attempts.
  const session = await raceSessionRead(
    desktopAuthBridge.getAuthSession(),
    AUTH0_DESKTOP_FALLBACK_SESSION_TIMEOUT_MS
  );
  if (session === null) {
    // Timed out or threw. Without a session-shape read there is no
    // positive evidence a session existed — don't resurrect a
    // persisted token.
    return null;
  }

  if (session.isAuthenticated !== false || session.hasRefreshToken !== true) {
    return null;
  }

  const persistedToken = getPersistedAuth0DesktopToken();
  if (!persistedToken) return null;
  return {
    accessToken: persistedToken,
    region: extractRegionFromAccessToken(persistedToken) ?? null,
  };
}

// ---------------------------------------------------------------------------
// Kinde
// ---------------------------------------------------------------------------

export function readKindeWeb(
  _options: ReadProviderOptions
): Promise<AuthSessionMetadata | null> {
  // Stub until the `add-kinde-session-invalidation` sibling ships
  // `/api/kinde-session` with the Auth0-parity contract. Returning null
  // maps to the bare-null outcome; the Kinde-web slice of the Flagsmith
  // rollout stays closed via segment rules until the sibling is live
  // (handled by the rollout owner, not by this stub).
  void _options;
  return Promise.resolve(null);
}

export async function readKindeDesktop({
  forceRefresh,
}: ReadProviderOptions): Promise<AuthSessionMetadata | null> {
  const bridge = getDefaultStore().get(kindeDesktopBridgeAtom);
  if (bridge === null) {
    // Bridge not mounted yet. The scheduler's mount prewarm runs as an
    // earlier-sibling effect than the bridge component's, so the very
    // first cold-boot fetch always lands here. Treat as transient so
    // `authStateAtom` stays `'loading'` and the boot-retry loop re-fires
    // once the bridge mounts — bare-null here would flash a definitive
    // signed-out state (isLoading=false, state=unauthenticated) for the
    // entire SDK-init window on every desktop-Kinde launch.
    throw new Error('AUTH_SESSION_TRANSIENT');
  }
  if (bridge.isLoading()) {
    // Bridge mounted but the Kinde SDK is still initialising. `getToken()`
    // can return null in this window before init completes; classifying
    // that as bare-null would merely move the signed-out flash from the
    // bridge-not-mounted window to the SDK-init window. Wait it out —
    // the boot-retry loop covers the ~1s SDK init.
    throw new Error('AUTH_SESSION_TRANSIENT');
  }
  const token = await bridge.getToken({ isForceRefresh: forceRefresh });
  if (token === null || token === undefined) {
    // Disambiguate via the SDK's authentication state. The desktop
    // bridge maps a v5 `refreshToken` failure (`{ success: false }`)
    // to null, so refresh FAILURE is indistinguishable from signed-out
    // at this surface. If the SDK still reports the session as authenticated
    // — its in-memory user object survives a swallowed refresh
    // failure — the null is a transient network blip: preserve the
    // cached state so a laptop-sleep + reconnect doesn't dump the
    // user to login. Only when the SDK itself reports unauthenticated
    // does the null count as genuine sign-out.
    if (bridge.isAuthenticated()) {
      // Known escalation gap on this branch: a revoked or absolutely-
      // expired refresh token is mapped by the desktop bridge to null
      // identically to a network blip — `getToken()` resolves null and
      // `isAuthenticated()` keeps reporting `true` because the SDK's
      // in-memory user object is only torn down through its own
      // logout flow, never on a refresh failure. Classifying the null
      // as transient (which is correct for blips) means a
      // definitively-dead session lands in a `'authenticated'` zombie
      // with no cached token — every consumer that reads the atoms
      // stays signed in, but every network request has no bearer.
      //
      // Today this is rescued externally: the axios interceptor still
      // reads the token from Redux, so on the first API call the 401
      // handler catches the empty response and fires the forced-
      // logout cleanup. Once that read path flips to the cache, this
      // branch has no rescuer and needs its own dead-session
      // escalation (bounded transient retries, then a positive
      // logout classification) before the legacy 401 rescue can be
      // safely removed. Tracked as a precondition on the read-path
      // flip.
      throw new Error('AUTH_SESSION_TRANSIENT');
    }
    return null;
  }
  return { accessToken: token, region: extractRegionFromAccessToken(token) ?? null };
}

// ---------------------------------------------------------------------------
// Keycloak
// ---------------------------------------------------------------------------

interface KeycloakSessionShape {
  accessToken?: string | null;
  user?: { name?: string | null; email?: string | null; [key: string]: unknown } | null;
  expires?: string;
  error?: string;
}

export async function readKeycloakWeb({
  forceRefresh,
  signal,
}: ReadProviderOptions): Promise<AuthSessionMetadata | null> {
  if (forceRefresh) {
    const bridge = getDefaultStore().get(keycloakSessionBridgeAtom);
    if (bridge !== null) {
      const updated = (await bridge.update({ refreshNow: true })) as
        | KeycloakSessionShape
        | null
        | undefined;
      // A resolved session goes through classification directly. A
      // null/undefined return does NOT — next-auth 4.24's `update()`
      // resolves `null` on any client fetch failure (its
      // `CLIENT_FETCH_ERROR` catch swallows the reject), so a
      // transport blip and a genuinely dead session produce the same
      // value here. Classifying null → bare-null would log out a
      // valid session on every 401-triggered force-refresh during a
      // hiccup, inverting the non-forced path's classification of the
      // identical network condition. Fall through to the normal-read
      // fetch instead; that disambiguates deterministically —
      // network blip → reject or non-OK → transient; signed out →
      // 200 empty session → bare-null; dead session →
      // `RefreshAccessTokenError` → definitive.
      if (updated) {
        return classifyKeycloakWebSession(updated);
      }
    }
    // Bridge not mounted (overlay window without SessionProvider, or
    // the sibling hasn't landed yet), or `update()` returned
    // null/undefined per the note above. Fall through to the
    // normal-read path — NextAuth's TTL refresh still applies; we
    // just can't pierce the TTL window from this caller.
  }

  const response = await fetch('/api/auth/session', {
    credentials: 'include',
    cache: 'no-store',
    signal,
  });
  if (!response.ok) {
    throw new Error('AUTH_SESSION_TRANSIENT');
  }
  const body = (await response.json()) as KeycloakSessionShape | null;
  return classifyKeycloakWebSession(body);
}

function classifyKeycloakWebSession(
  session: KeycloakSessionShape | null
): AuthSessionMetadata | null {
  if (session === null) {
    return null;
  }
  if (session.error === 'RefreshAccessTokenError') {
    throw new Error('AUTH_SESSION_INVALID');
  }
  const token = session.accessToken ?? null;
  if (token === null) {
    return null;
  }
  return { accessToken: token, user: session.user ?? null };
}

export async function readKeycloakDesktop({
  forceRefresh,
  generation,
}: ReadProviderOptions): Promise<AuthSessionMetadata | null> {
  // `launchFetch` always passes `generation`; provider-branch tests
  // and any direct callers that skip it get treated as generation 0
  // — same-generation joiners still work within a single test's
  // scope, and cross-generation is exercised explicitly via the
  // reset helper. This is defensive; the cache's own contract is
  // "generation is always plumbed".
  const gen = generation ?? 0;
  // Direct call to the module-level singleton — NOT a bridge atom.
  // A bridge atom would risk transitioning a valid session to
  // unauthenticated during bootstrap if the bridge component hasn't
  // mounted yet; the UserManager is process-wide and module-importable,
  // so we read it on demand.
  const userManager = getKeycloakUserManager();

  if (forceRefresh) {
    try {
      // Route through the same single-flight guard the expired-user
      // branch uses. A forced refresh that lands while an
      // expired-branch renewal is in flight is legitimate: joining
      // the existing attempt is a fresh grant. Calling
      // `userManager.signinSilent()` raw here would issue a second
      // concurrent refresh-token grant; under rotation the loser
      // receives `invalid_grant`, which classifies definitive and
      // logs the user out during boot.
      const renewed = await performExpiredUserRenew(userManager, gen);
      const token = renewed?.access_token ?? null;
      return token === null ? null : { accessToken: token };
    } catch (err) {
      // Classify token-endpoint errors. RFC 6749 §5.2 definitive shapes
      // (invalid_grant, invalid_client, unauthorized_client) are the
      // Keycloak-desktop parity for Keycloak-web's RefreshAccessTokenError
      // signal. Anything else (network, parse, transport) re-throws
      // raw — the cache classifies it as transient.
      if (isDefinitiveOidcError(err)) {
        throw new Error('AUTH_SESSION_INVALID');
      }
      throw err;
    }
  }

  const user = await userManager.getUser();
  if (user === null) {
    // Storage empty — no persisted session at all. Genuinely signed
    // out.
    return null;
  }
  if (user.expired === true) {
    // Locally-stored access token has passed `expires_at` but the
    // refresh token is still present in storage. Keycloak access
    // tokens are short-lived (minutes), so nearly every cold boot
    // after a short absence lands here.
    //
    // Bare-null here would produce a definitive signed-out state
    // (`isLoading=false`) with no healing trigger — bare-null doesn't
    // arm the boot-retry loop, and a fresh window already has focus
    // so the focus refetch never fires. `useIsAuthenticated` has no
    // Keycloak carve-out to catch the flash.
    //
    // The cache drives an explicit `signinSilent()` here (via the
    // single-flight guard). The UserManager's `automaticSilentRenew`
    // does NOT cover this case: it subscribes only to the pre-expiry
    // `accessTokenExpiring` event, and `AccessTokenEvents.load()`
    // cancels that timer when the loaded `duration <= 0`. Without an
    // explicit renewal from the cache, the atom would hold `'loading'`
    // forever.
    //
    // The single-flight guard prevents concurrent triggers (scheduler
    // prewarm + focus refetch + boot-retry) from firing overlapping
    // refresh-token grants that would race under rotation. The guard
    // clears on settle so a later trigger can re-attempt after a
    // transient failure — a once-per-process guard would strand the
    // user on a single network blip. See guard docstring above for
    // the rotation-safety rationale.
    try {
      const renewed = await performExpiredUserRenew(userManager, gen);
      const token = renewed?.access_token ?? null;
      if (token === null) {
        // Renewer resolved but produced no token — treat as transient.
        // Storage still holds an expired user; no positive session-
        // death signal has been observed.
        throw new Error('AUTH_SESSION_TRANSIENT');
      }
      return { accessToken: token };
    } catch (err) {
      if (err instanceof Error && err.message === 'AUTH_SESSION_TRANSIENT') {
        throw err;
      }
      if (isDefinitiveOidcError(err)) {
        // Throw the definitive marker WITHOUT eagerly calling
        // `userManager.removeUser()`. An earlier version of this
        // path removed the stored user here to prevent pre-login
        // readers from re-observing the dead session — but
        // `signoutRedirect()` (called downstream by the KEYCLOAK
        // forced-logout branch) captures the stored user's
        // `id_token` as the `id_token_hint` on the end-session
        // request, and Keycloak requires that hint to end the
        // session silently (without it, the user lands on a
        // logout-confirmation page). oidc-client-ts's own signout
        // flow captures the hint and THEN removes the user
        // atomically; an eager removal here would strip the hint
        // before the redirect fires.
        throw new Error('AUTH_SESSION_INVALID');
      }
      throw new Error('AUTH_SESSION_TRANSIENT');
    }
  }
  const token = user.access_token ?? null;
  return token === null ? null : { accessToken: token };
}
